import { cva, type VariantProps } from 'class-variance-authority';
import type { ComponentProps } from 'react';
import { cn } from '../../common/functions/cn';

const buttonV2Variants = cva(
  [
    'group/button inline-flex shrink-0 items-center justify-center whitespace-nowrap rounded-md border bg-clip-padding',
    'font-medium text-sm',
    'select-none outline-none transition-all',
    // Popup triggers stay put: a button that shifts as its panel opens reads as jitter.
    '[&:active:not([aria-haspopup],[popovertarget],[commandfor])]:translate-y-px',
    'focus-visible:border-ring focus-visible:ring-2 focus-visible:ring-ring/50',
    'aria-invalid:border-error aria-invalid:ring-2 aria-invalid:ring-error/20 aria-invalid:focus-visible:border-error aria-invalid:focus-visible:ring-error/60 dark:aria-invalid:border-error/50 dark:aria-invalid:ring-error/50',
    'disabled:pointer-events-none disabled:opacity-50',
    "[&_svg:not([class*='size-'])]:size-4 [&_svg]:pointer-events-none [&_svg]:shrink-0",
  ],
  {
    variants: {
      variant: {
        default:
          'border-transparent bg-primary text-primary-foreground hover:bg-primary/90 aria-expanded:bg-primary/90',
        outline:
          'border-border hover:bg-muted hover:text-foreground aria-expanded:border-ring aria-expanded:bg-accent aria-expanded:text-accent-foreground',
        secondary:
          'border-transparent bg-foreground text-background [--secondary-active:color-mix(in_oklch,var(--foreground),var(--background)_25%)] hover:bg-(--secondary-active) aria-expanded:bg-(--secondary-active)',
        ghost:
          'border-transparent hover:bg-muted hover:text-foreground aria-expanded:bg-accent aria-expanded:text-accent-foreground aria-expanded:hover:bg-accent',
        destructive:
          'border-transparent bg-error/10 text-error [--destructive-ink:color-mix(in_oklch,var(--error),var(--foreground)_30%)] hover:bg-error/30 hover:text-(--destructive-ink) focus-visible:border-error/40 focus-visible:ring-error/20 aria-expanded:bg-error/30 aria-expanded:text-(--destructive-ink) dark:bg-error/20 dark:focus-visible:ring-error/40',
        link: 'border-transparent text-primary underline-offset-4 hover:underline',
      },
      size: {
        default: 'h-8 gap-1.5 px-2.5 has-data-[icon=inline-end]:pr-2 has-data-[icon=inline-start]:pl-2',
        xs: "h-6 gap-1 in-data-[slot=button-group]:rounded-md rounded-[min(var(--radius-md),10px)] px-2 text-xs has-data-[icon=inline-end]:pr-1.5 has-data-[icon=inline-start]:pl-1.5 [&_svg:not([class*='size-'])]:size-3",
        sm: "h-7 gap-1 in-data-[slot=button-group]:rounded-md rounded-[min(var(--radius-md),12px)] px-2.5 text-[0.8rem] has-data-[icon=inline-end]:pr-1.5 has-data-[icon=inline-start]:pl-1.5 [&_svg:not([class*='size-'])]:size-3.5",
        lg: 'h-9 gap-1.5 px-2.5 has-data-[icon=inline-end]:pr-2 has-data-[icon=inline-start]:pl-2',
        icon: 'size-8',
        'icon-xs':
          "size-6 in-data-[slot=button-group]:rounded-md rounded-[min(var(--radius-md),10px)] [&_svg:not([class*='size-'])]:size-3",
        'icon-sm':
          "size-7 in-data-[slot=button-group]:rounded-md rounded-[min(var(--radius-md),12px)] [&_svg:not([class*='size-'])]:size-3.5",
        'icon-lg': 'size-9',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
);

export type ButtonV2Props = ComponentProps<'button'> & VariantProps<typeof buttonV2Variants>;

const ButtonV2 = ({ className, variant = 'default', size = 'default', ...props }: ButtonV2Props) => (
  <button data-slot="button" type="button" className={cn(buttonV2Variants({ variant, size, className }))} {...props} />
);

export { ButtonV2, buttonV2Variants };
